import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Supplier } from "../../services/supplierService";

export interface SupplierState {
  suppliers: Supplier[];
  currentSupplier: Supplier | null;
  loading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalSuppliers: number;
    hasNext: boolean;
    hasPrev: boolean;
  } | null;
  stats: {
    totalSuppliers: number;
    activeSuppliers: number;
    inactiveSuppliers: number;
    paymentTermsBreakdown: Array<{ _id: string; count: number }>;
  } | null;
}

const initialState: SupplierState = {
  suppliers: [],
  currentSupplier: null,
  loading: false,
  error: null,
  pagination: null,
  stats: null,
};

const supplierSlice = createSlice({
  name: "suppliers",
  initialState,
  reducers: {
    // Get suppliers actions
    getSuppliersStart: (
      state,
      action: PayloadAction<{
        page?: number;
        limit?: number;
        search?: string;
        isActive?: boolean;
      }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    getSuppliersSuccess: (
      state,
      action: PayloadAction<{
        suppliers: Supplier[];
        pagination: any;
      }>
    ) => {
      state.loading = false;
      state.suppliers = action.payload.suppliers;
      state.pagination = action.payload.pagination;
      state.error = null;
    },
    getSuppliersFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Get single supplier actions
    getSupplierStart: (state, action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },
    getSupplierSuccess: (state, action: PayloadAction<Supplier>) => {
      state.loading = false;
      state.currentSupplier = action.payload;
      state.error = null;
    },
    getSupplierFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Create supplier actions
    createSupplierStart: (state, action: PayloadAction<any>) => {
      state.loading = true;
      state.error = null;
    },
    createSupplierSuccess: (state, action: PayloadAction<Supplier>) => {
      state.loading = false;
      state.suppliers.unshift(action.payload);
      state.error = null;
    },
    createSupplierFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Update supplier actions
    updateSupplierStart: (
      state,
      action: PayloadAction<{ id: string; data: any }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    updateSupplierSuccess: (state, action: PayloadAction<Supplier>) => {
      state.loading = false;
      const index = state.suppliers.findIndex(
        (s) => s._id === action.payload._id
      );
      if (index !== -1) {
        state.suppliers[index] = action.payload;
      }
      if (state.currentSupplier?._id === action.payload._id) {
        state.currentSupplier = action.payload;
      }
      state.error = null;
    },
    updateSupplierFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Delete supplier actions
    deleteSupplierStart: (state, action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },
    deleteSupplierSuccess: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.suppliers = state.suppliers.filter((s) => s._id !== action.payload);
      if (state.currentSupplier?._id === action.payload) {
        state.currentSupplier = null;
      }
      state.error = null;
    },
    deleteSupplierFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Get supplier stats actions
    getSupplierStatsStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    getSupplierStatsSuccess: (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.stats = action.payload;
      state.error = null;
    },
    getSupplierStatsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Clear current supplier
    clearCurrentSupplier: (state) => {
      state.currentSupplier = null;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  getSuppliersStart,
  getSuppliersSuccess,
  getSuppliersFailure,
  getSupplierStart,
  getSupplierSuccess,
  getSupplierFailure,
  createSupplierStart,
  createSupplierSuccess,
  createSupplierFailure,
  updateSupplierStart,
  updateSupplierSuccess,
  updateSupplierFailure,
  deleteSupplierStart,
  deleteSupplierSuccess,
  deleteSupplierFailure,
  getSupplierStatsStart,
  getSupplierStatsSuccess,
  getSupplierStatsFailure,
  clearCurrentSupplier,
  clearError,
} = supplierSlice.actions;

export default supplierSlice.reducer;
